import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { FormActions } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { getCompanyModules, updateCompanyModules } from '@/features/settings/api';
import { Toggle } from '@/features/settings/Toggle';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

// 8 fixed module keys (PLAN-FASE3.md WP-00)
const MODULES = [
  'vitality',
  'participation',
  'pilots',
  'challenges',
  'communication',
  'documents',
  'reports',
  'ai_coach',
] as const;

/**
 * `/settings/modules` — Instellingen › Modules (PLAN-FASE3.md WP-00): 8
 * real Toggle rows, one per module, with optimistic update like
 * SettingsPrivacyPage.tsx. Admin only (canUpdateCompanySettings).
 */
export function SettingsModulesPage() {
  const { t } = useTranslation();
  const { company } = useActiveCompany();
  const { permissions } = useMe();
  const companyId = company ? company.id : null;

  const state = useAsync(
    () => (companyId ? getCompanyModules(companyId) : Promise.reject(new Error('no company'))),
    [companyId],
  );

  // Track local state for each module's enabled toggle
  const [modules, setModules] = useState<Record<string, boolean | undefined>>({});

  const form = useSubmit(async () => {
    if (!companyId || !state.data) return;

    // Build the update body with only modules that were changed
    const updateBody: Record<string, boolean> = {};
    for (const key of MODULES) {
      if (modules[key] !== undefined && modules[key] !== state.data[key]) {
        updateBody[key] = modules[key]!;
      }
    }

    if (Object.keys(updateBody).length === 0) return; // Nothing to update

    await updateCompanyModules(companyId, updateBody);
    state.reload();
    setModules({}); // Reset local state
  });

  const canEdit = permissions.canUpdateCompanySettings;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader title={t('settings.modules.title')} lead={t('settings.modules.lead')} />

      {!companyId && <p className="text-accent text-sm">{t('settings.noCompany')}</p>}
      {companyId && (
        <Card>
          <CardHeader title={t('settings.modules.title')} />
          <AsyncSection state={state} label={t('settings.modules.title')} skeleton="card">
            {(data) => (
              <form
                className="flex flex-col gap-6"
                onSubmit={(event) => {
                  event.preventDefault();
                  void form.submit();
                }}
              >
                <div className="border-line divide-line flex flex-col divide-y border-t">
                  {MODULES.map((moduleKey) => (
                    <Toggle
                      key={moduleKey}
                      label={t(`settings.modules.${moduleKey}`)}
                      hint={t(`settings.modules.${moduleKey}Hint`)}
                      checked={
                        modules[moduleKey] !== undefined
                          ? modules[moduleKey]!
                          : (data[moduleKey] ?? true)
                      }
                      onChange={(value) => setModules((prev) => ({ ...prev, [moduleKey]: value }))}
                      disabled={!canEdit}
                    />
                  ))}
                </div>

                {!canEdit && <p className="text-accent text-[13px]">{t('settings.adminOnly')}</p>}
                {canEdit && (
                  <>
                    <SubmitError error={form.error} />
                    <FormActions>
                      <Button type="submit" disabled={form.pending}>
                        {t('settings.save')}
                      </Button>
                    </FormActions>
                  </>
                )}
              </form>
            )}
          </AsyncSection>
        </Card>
      )}
    </section>
  );
}
